// Admin Page Users server-state hooks: the list query plus the create/update/delete,
// password-reset and invitation-resend mutations. Mutations own the draft→API mapping, toast feedback, and cache
// invalidation;

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import {
  createAdminUser,
  deleteAdminUser,
  fetchAdminUsers,
  fetchAdminUserLanguage,
  resendAdminUserInvitation,
  sendAdminUserPasswordReset,
  updateAdminUser,
  UserCreateConflictError,
  type AdminUserLanguage,
  type IAdminUser,
} from '@apis/adminUsers';
import type { IToast } from '@components/ui/Toasts/useToasts';
import type { IUserDraft, UserLanguage } from '../UserDialog/UserDialog';
import { OWNS_ERROR_FEEDBACK } from '@d-orca/shared/client/hooks';

const adminUsersKey = (siteId: string) => ['admin', 'users', siteId] as const;

/** A user's stored UI language — the edit dialog's prefill read; invalidated
 *  by the update mutation when it writes a new language. */
const adminUserLanguageKey = (siteId: string, username: string) =>
  ['admin', 'users', siteId, 'language', username] as const;

type ToastPush = (toast: Omit<IToast, 'id'>) => void;

export function useAdminUsers(siteId: string | null) {
  return useQuery({
    queryKey: adminUsersKey(siteId ?? ''),
    queryFn: ({ signal }) => fetchAdminUsers(siteId!, signal),
    enabled: !!siteId,
    staleTime: 60_000,
    refetchOnWindowFocus: false,
  });
}

/** Dialog language option → upstream preference constant. */
function toUpstreamLanguage(language: UserLanguage): AdminUserLanguage {
  return language === 'ja' ? 'jpn' : 'en-us';
}

/** Register-with-invite; toasts "invitation sent to {email}" / conflict / error. */
export function useCreateAdminUser(siteId: string | null, push: ToastPush) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (draft: IUserDraft) =>
      createAdminUser(siteId!, {
        name: draft.fullName,
        email: draft.email,
        phone: draft.phone || null,
        role: draft.permission!,
        ...(draft.permission === 'VIEWER'
          ? { permittedProfessionsIds: draft.permittedProfessionsIds }
          : {}),
        ...(draft.language ? { language: toUpstreamLanguage(draft.language) } : {}),
      }),
    onSuccess: async (_created, draft) => {
      push({
        kind: 'success',
        title: t('adminUsers.createSuccess'),
        sub: t('adminUsers.createSuccessDetail', { email: draft.email }),
      });
      await queryClient.invalidateQueries({ queryKey: adminUsersKey(siteId!) });
    },
    onError: (err) => {
      push({
        kind: 'error',
        title: t(
          err instanceof UserCreateConflictError
            ? 'adminUsers.createConflict'
            : 'adminUsers.createError',
        ),
      });
    },
  });
}

export function useUpdateAdminUser(siteId: string | null, push: ToastPush) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: ({ username, draft }: { username: string; draft: IUserDraft }) =>
      updateAdminUser(siteId!, username, {
        name: draft.fullName,
        phone: draft.phone || null,
        ...(draft.language ? { language: toUpstreamLanguage(draft.language) } : {}),
        ...(draft.permission
          ? {
              role: draft.permission,
              ...(draft.permission === 'VIEWER'
                ? { permittedProfessionsIds: draft.permittedProfessionsIds }
                : {}),
            }
          : {}),
      }),
    onSuccess: async (_data, { username, draft }) => {
      push({ kind: 'success', title: t('adminUsers.updateSuccess') });
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: adminUsersKey(siteId!) }),
        // The edit may have changed the stored language — refresh the prefill.
        ...(draft.language
          ? [queryClient.invalidateQueries({ queryKey: adminUserLanguageKey(siteId!, username) })]
          : []),
      ]);
    },
    onError: () => {
      push({ kind: 'error', title: t('adminUsers.updateError') });
    },
  });
}
/** Removes a member from the project; toasts success/error and refreshes the list. */
export function useDeleteAdminUser(siteId: string | null, push: ToastPush) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (username: string) => deleteAdminUser(siteId!, username),
    onSuccess: async () => {
      push({ kind: 'success', title: t('adminUsers.deleteSuccess') });
      await queryClient.invalidateQueries({ queryKey: adminUsersKey(siteId!) });
    },
    onError: () => {
      push({ kind: 'error', title: t('adminUsers.deleteError') });
    },
  });
}

interface IMemberMailOptions {
  send: (siteId: string, username: string) => Promise<void>;
  sentKey: string;
  errorKey: string;
  /** Whether the mail changes what the list shows (an invitation renews the member's status). */
  refreshesList: boolean;
}

/** Mails a member something by username; toasts "sent to {email}" / error. */
function useMemberMail(siteId: string | null, push: ToastPush, options: IMemberMailOptions) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  return useMutation({
    meta: OWNS_ERROR_FEEDBACK,
    mutationFn: (user: IAdminUser) => options.send(siteId!, user.username),
    onSuccess: async (_data, user) => {
      push({
        kind: 'success',
        title: t(options.sentKey),
        sub: t('adminUsers.createSuccessDetail', { email: user.email }),
      });
      if (options.refreshesList) await queryClient.invalidateQueries({ queryKey: adminUsersKey(siteId!) });
    },
    onError: () => {
      push({ kind: 'error', title: t(options.errorKey) });
    },
  });
}

/** Mails a member a password-reset link. Nothing to refresh. */
export function useSendPasswordReset(siteId: string | null, push: ToastPush) {
  return useMemberMail(siteId, push, {
    send: sendAdminUserPasswordReset,
    sentKey: 'adminUsers.resetPasswordSent',
    errorKey: 'adminUsers.resetPasswordError',
    refreshesList: false,
  });
}

/** Re-sends a member's invitation, then refreshes the list — a resend renews an expired one. */
export function useResendInvitation(siteId: string | null, push: ToastPush) {
  return useMemberMail(siteId, push, {
    send: resendAdminUserInvitation,
    sentKey: 'adminUsers.invitationResent',
    errorKey: 'adminUsers.invitationResendError',
    refreshesList: true,
  });
}

/** Upstream preference constants → the dialog's language options. */
function toDialogLanguage(upstream: string | null): UserLanguage | null {
  if (upstream === 'en-us') return 'en';
  if (upstream === 'jpn') return 'ja';
  return null;
}

/**
 * The stored UI language of the user being edited — undefined while loading,
 * null when the user never saved one.
 */
export function useAdminUserLanguage(
  siteId: string | null,
  username: string | null,
): UserLanguage | null | undefined {
  const { data } = useQuery({
    queryKey: adminUserLanguageKey(siteId ?? '', username ?? ''),
    queryFn: ({ signal }) => fetchAdminUserLanguage(siteId!, username!, signal),
    enabled: !!siteId && !!username,
    refetchOnWindowFocus: false,
  });
  return data === undefined ? undefined : toDialogLanguage(data);
}
